/**
 * PoolMetricsTable - Currency pool metrics table
 *
 * Renders the sortable data table showing all pool metrics:
 * Token ID, supply figures, rates, fees, variation, and USDb allocation.
 *
 * @param {{ pools: Array, reserveRows: Array }} props
 */

import React from "react";
import { commonStyles, colors } from "../../AgenticPaymentTransactionHistory/styles";
import { formatNumber, formatRate, formatFee } from "../utils";
import { HelpTitle } from "../../AdminHeaderHelp";

/** What the "?" after each header says, from the signing server's pool formulas. */
const HEADER_HELP = {
    initialSupply: "Tokens created when the currency was set up.",
    totalSupply: "Initial supply plus minted, minus burned.",
    circulatingSupply: "Tokens held by users.",
    usdbAllocation: "USDb this pool holds.",
    buyRate: "The rate a user gets buying this currency with USDb.",
    sellRate: "The rate a user gets selling this currency for USDb.",
    priceRate: "1 ÷ buy rate: what one token costs in USDb at the buy rate.",
    pricePool: "USDb allocation ÷ circulating supply: the USDb behind each token.",
    variation: "How far the price pool is from the price rate, as a percent.",
    baseFee: "The fee on every swap in this currency.",
    dynamicFee: "The base fee, raised as the variation grows. USD-backed pools keep the base fee.",
};

const Header = ({ title, help, style }) => (
    <th style={style}><HelpTitle title={title} help={help} /></th>
);

// The table scrolls in its own box so the header row can stay in view.
const scrollBoxStyle = { overflow: "auto", maxHeight: "calc(100vh - 120px)" };
const stickyHeaderStyle = { position: "sticky", top: 0, zIndex: 2 };

const reserveRowStyle = { backgroundColor: "#fff7e6", fontWeight: 600 };

/** USDb or USDC: supply only. Rates, fees and allocation do not apply to the reserve. */
const ReserveRow = ({ row }) => {
    const supply = (val) => (val == null ? "-" : formatNumber(val));
    return (
        <tr style={reserveRowStyle}>
            <td>
                <a
                    href={`/admin/token_holders?token_id=${encodeURIComponent(row.tokenId)}`}
                    title="See who holds this token"
                >
                    <code>{row.tokenId}</code>
                </a>
            </td>
            <td>{row.currencyName}</td>
            <td>{supply(row.initialSupply)}</td>
            <td>{supply(row.totalSupply)}</td>
            <td>{supply(row.circulatingSupply)}</td>
            {Array.from({ length: 8 }, (_, i) => (
                <td key={i}>-</td>
            ))}
        </tr>
    );
};

const PoolMetricsTable = ({ pools, reserveRows = [] }) => {
    if (!pools || pools.length === 0) {
        return (
            <div style={commonStyles.panel}>
                <div className="index_table_container">
                    <table className="index_table">
                        <thead>
                            <tr>
                                <th>Token ID</th>
                                <th>Currency Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td
                                    colSpan="2"
                                    style={{
                                        textAlign: "center",
                                        padding: "40px",
                                        color: colors.gray500,
                                    }}
                                >
                                    No currencies match your filters.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div style={commonStyles.panel}>
            {/* Inline: the admin page loads this pack's JS but not its CSS, so styles.scss never applies there. */}
            <div className="index_table_container" style={scrollBoxStyle}>
                <table className="index_table">
                    <thead>
                        <tr>
                            <th style={stickyHeaderStyle}>Token ID</th>
                            <th style={stickyHeaderStyle}>Currency Name</th>
                            <Header style={stickyHeaderStyle} title="Initial Supply" help={HEADER_HELP.initialSupply} />
                            <Header style={stickyHeaderStyle} title="Total Supply" help={HEADER_HELP.totalSupply} />
                            <Header style={stickyHeaderStyle} title="Circulating Supply" help={HEADER_HELP.circulatingSupply} />
                            <Header style={stickyHeaderStyle} title="USDb Allocation" help={HEADER_HELP.usdbAllocation} />
                            <Header style={stickyHeaderStyle} title="Buy Rate" help={HEADER_HELP.buyRate} />
                            <Header style={stickyHeaderStyle} title="Sell Rate" help={HEADER_HELP.sellRate} />
                            <Header style={stickyHeaderStyle} title="Price Rate" help={HEADER_HELP.priceRate} />
                            <Header style={stickyHeaderStyle} title="Price Pool" help={HEADER_HELP.pricePool} />
                            <Header style={stickyHeaderStyle} title="Variation (%)" help={HEADER_HELP.variation} />
                            <Header style={stickyHeaderStyle} title="Base Fee" help={HEADER_HELP.baseFee} />
                            <Header style={stickyHeaderStyle} title="Dynamic Fee" help={HEADER_HELP.dynamicFee} />
                        </tr>
                    </thead>
                    <tbody>
                        {reserveRows.map((row) => (
                            <ReserveRow key={row.tokenId} row={row} />
                        ))}
                        {pools.map((pool, idx) => {
                            const variation = pool.variation || 0;
                            const variationClass =
                                variation > 0
                                    ? "positive"
                                    : variation < 0
                                        ? "negative"
                                        : "neutral";

                            return (
                                <tr
                                    key={pool.tokenId}
                                    className={idx % 2 === 0 ? "even" : "odd"}
                                >
                                    <td>
                                        {/* Straight to who holds this token.
                                            The table answers how much of a
                                            currency exists; the next question
                                            is always who is holding it. */}
                                        <a
                                            href={`/admin/token_holders?token_id=${encodeURIComponent(
                                                pool.tokenId,
                                            )}`}
                                            title="See who holds this token"
                                        >
                                            <code>{pool.tokenId}</code>
                                        </a>
                                    </td>
                                    <td
                                        title={pool.currencyName}
                                        style={{
                                            maxWidth: "200px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {pool.stale && (
                                            <span
                                                title={`Chain data could not be fetched; showing the last numbers${pool.chainSyncedAt ? ` from ${new Date(pool.chainSyncedAt).toLocaleString()}` : ""}.`}
                                                aria-label="Stale chain data"
                                                style={{ color: "#b45309", marginRight: "6px" }}
                                            >
                                                ⚠
                                            </span>
                                        )}
                                        {pool.currencyName}
                                    </td>
                                    <td>{formatNumber(pool.initialSupply)}</td>
                                    <td>{formatNumber(pool.totalSupply)}</td>
                                    <td>{formatNumber(pool.circulatingSupply)}</td>
                                    <td style={{ whiteSpace: "nowrap" }}>{formatNumber(pool.usdbAllocation)}</td>
                                    <td>{pool.buyRate != null ? pool.buyRate : "-"}</td>
                                    <td>{pool.sellRate != null ? pool.sellRate : "-"}</td>
                                    <td>{formatRate(pool.priceRate)}</td>
                                    <td>{formatRate(pool.pricePool)}</td>
                                    <td>
                                        <span className={`variation-tag ${variationClass}`}>
                                            {variation >= 0 ? "+" : ""}
                                            {variation.toFixed(7)}%
                                        </span>
                                    </td>
                                    <td>{formatFee(pool.baseFee)}</td>
                                    <td>{formatFee(pool.dynamicFee, 7)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PoolMetricsTable;
